<script setup>
const { model } = defineProps({
  model: { type: Object, required: true },
})
const defaultMaxProducts = 10
const query = computed(() => ({
  ProductCategories: model.catalogType?.value,
  ...buildQuery(),
}))

const shouldFetchProducts = computed(() => {
  const maxProducts = model.productMaximum?.value ?? defaultMaxProducts
  const featuredCount = model.featuredProducts?.length ?? 0

  return (
    !!query.value.ProductCategories
    && featuredCount < maxProducts
  )
})

const asyncKey = computed(() =>
  // unique key to avoid duplicate keys across product listing blocks appearing on the same page
  // allows a safe skip of the fetch when more products are not needed
  `product-listing-${model.contentLink?.guidValue}-${JSON.stringify(query.value)}`,
)

const { data } = await useAsyncData(
  () => asyncKey.value,
  async () => {
    if (!shouldFetchProducts.value) {
      return { products: [] }
    }

    return $fetch('/api/products', {
      query: query.value,
    })
  },
)

const products = computed(() => data.value?.products ?? [])

function buildQuery() {
  const query = {}

  model.productCategories?.expandedValue?.forEach((category) => {
    const key = category.categoryInternal?.value
    if (!key)
      return

    const values = (category.options?.expandedValue ?? [])
      .map(o => o.uriValueInternal?.value)
      .filter(Boolean)

    if (values.length) {
      query[key] = values.join(',')
    }
  })

  if (model.productMaximum?.value) {
    query.PageSize = String(model.productMaximum.value)
  }

  return query
}

const displayedProducts = computed(() => {
  const featured = model.featuredProducts ?? []
  const searchResults = products.value

  const featuredIds = new Set(featured.map(p => p.id))

  const searched = searchResults.filter(
    p => !featuredIds.has(p.id),
  )

  return [...featured, ...searched]
    .slice(0, model.productMaximum?.value ?? defaultMaxProducts)
})

const showContent = computed(() => {
  return model.title?.value || model.body?.value
})
</script>

<template>
  <div
    class="product-listing-block block-spacing"
    :class="[
      model.class,
    ]"
  >
    <div class="container-fluid">
      <div class="plb__listing-wrapper flex flex-col xl:flex-row gap-[30px] items-start mr-auto ml-auto">
        <div
          v-if="showContent"
          class="plb__content prose md:basis-1/3"
        >
          <h2 v-if="model.title?.value">
            {{ model.title.value }}
          </h2>
          <div v-html="model.body?.value" />
          <UButton
            v-if="model.ctaLink?.value?.href && model.ctaLink?.value?.text"
            variant="solid"
            color="primary"
            :to="useCmsUrl(model.ctaLink.value.href)"
            :locale="false"
            class="w-full sm:w-auto no-underline justify-center"
            :label="model.ctaLink.value.text"
          />
        </div>
        <div class="plb__products w-full prose">
          <div
            class="plb__list-wrapper"
            :class="[{ 'no-content': !showContent }]"
          >
            <div
              v-if="displayedProducts?.length > 0"
              class="plb__products-list"
            >
              <ProductListItem
                v-for="product in displayedProducts"
                :key="product.title?.fragments ? product.title.fragments[0].internalFormat : product.title"
                :model="product"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  @use '@/assets/css/blocks/product-listing-block.scss';
</style>
